/* eslint-disable no-underscore-dangle */
import { CardInstance } from './card.service';
import { FieldInstance } from './field.service';
import { PageInstance } from './page.service';
import { CoreCustomPageType, CorePageReviewRendererType, LogicFnRef } from './workflow.type';

/**
 * An extension helper to Workflow page class
 * By default it process only visible cards and fields
 */
export class PageHelper {
  /**
   * Current Validation state
   */
  private _currentValidationState = true;

  /** submit status */
  private _submitStatus: boolean;

  constructor(private readonly page: PageInstance) {}

  /**
   * return list of invalid fields in the page, by default it filters visible fields
   */
  public get invalidCards(): CardInstance[] {
    return this.pageVisibleCards.filter((c) => !c.valid);
  }

  /**
   * return list of invalid fields in the page, by default it filters visible fields
   */
  public get invalidFields(): FieldInstance[] {
    return this.pageVisibleFields.filter((f) => !f.valid);
  }

  /**
   * Indicate if the page is complete
   */
  public get isPageComplete(): boolean {
    return this.invalidFields.length === 0 && this.invalidCards.length === 0 && this.page.valid;
  }

  /**
   * Indicate if the page is touched
   */
  public get isPageTouched(): boolean {
    return this.touchedFields.length > 0 || this.page.dirty;
  }

  /** indicate previous button visible */
  public get isPreviousButtonVisible(): boolean {
    return (this.hasPreviousPage || this.page.config.alwaysShowPagePreviousButton) && !this.page.hidePagePreviousButton;
  }

  public get isNextButtonVisible(): boolean {
    return (this.hasNextPage || this.page.config.alwaysShowPageNextButton) && !this.page.hidePageNextButton;
  }

  /** indicate if has previous page */
  public get hasPreviousPage(): boolean {
    return !!this.page.previousPage;
  }

  /** indicate if there is a next page */
  public get hasNextPage(): boolean {
    return !!this.page.nextPage;
  }

  /** indicate if page has progress bar */
  public get hasProgressBar(): boolean | LogicFnRef[] {
    // By default we want to show the progress bar
    if (this.page.config.showProgressBar === undefined) {
      return true;
    }
    return this.page.config.showProgressBar;
  }

  /**
   * return list of fields that are touched in the page, by default it filters visible fields
   */
  public get touchedFields(): FieldInstance[] {
    return this.pageVisibleFields.filter((f) => f.dirty);
  }

  public get customPageType(): CoreCustomPageType {
    const defaultCustomPageType: CoreCustomPageType = this.page.config.isReviewPage
      ? 'standard-review'
      : this.page.defaultPageType
      ? this.page.defaultPageType
      : 'standard';
    return this.page.config.customPageType || defaultCustomPageType;
  }

  public get reviewRendererType(): CorePageReviewRendererType {
    const defaultRendererType: CorePageReviewRendererType = 'standard-page-review-renderer';
    return this.page.config.reviewRendererType || defaultRendererType;
  }

  public get submitFailed(): boolean {
    // eslint-disable-next-line eqeqeq
    return this._submitStatus == false;
  }

  public set submitStatus(v: boolean) {
    this._submitStatus = v;
  }

  /**
   * return true if all the required pages in current flow are complete.
   * this method will only scan visible pages.
   */
  public allRequiredPagesComplete(currentFlowPages: PageInstance[], throwOnMissingPage?: boolean): boolean {
    const requiredPages = this.getRequiredPages(currentFlowPages, throwOnMissingPage);
    if (requiredPages.length === 0) {
      return true;
    }

    return requiredPages.filter((p) => p.visible && !p.helper.isPageComplete).length === 0;
  }

  /**
   * return list of required page in the current flow. If the required page is invisible, it will be ignored.
   * Note for iterative pages:
   *    In case of iterative page, pid in requiredPageIds will have # which will be different with actual page.id, for example
   *    if we have three iterative pages and the requiredPageId is personal-#,
   *    in the flow we will have three personal pages with ids: personal-0, personal-1, personal-2.
   *    All these pages will have common page.config.id which is personal-#.
   *    As a result when we want to find all these pages in the current flow, we need to use p.config.id to filter
   *    e. g. currentFlow.pages.find( page => page.config.id === requiredPageId)
   */
  public getRequiredPages(currentFlowPages: PageInstance[], throwOnMissingPage?: boolean): PageInstance[] {
    const requiredPageIds = this.page.config.requiredPages || [];
    if (requiredPageIds.length === 0) {
      return [];
    }

    if (!currentFlowPages) {
      throw new Error(`Current flow pages Cannot be null to process all the required pages of '${this.page.id}'`);
    }

    const pages: PageInstance[] = [];
    for (const requiredPageId of requiredPageIds) {
      const pagesInFlow: PageInstance[] = currentFlowPages.filter((page) => page.config.id === requiredPageId);
      if (pagesInFlow.length === 0 && throwOnMissingPage) {
        throw new Error(`Invalid requiredPage Id in'${this.page.id}'.
                The page id '${requiredPageId}' does not exist in current workflow pages'`);
      }

      pagesInFlow.forEach((pageInFlow) => {
        if (pageInFlow?.visible) {
          pages.push(pageInFlow);
        }
      });
    }
    return pages;
  }

  /**
   * Call the call back
   */
  public whenValidationChange(callback: () => void): void {
    const changed = this._currentValidationState !== this.page.helper.isPageComplete;
    if (changed) {
      this._currentValidationState = this.page.helper.isPageComplete;
      callback();
    }
  }

  /**
   * return list of all visible cards in the page
   */
  public get pageVisibleCards(): CardInstance[] {
    return this.page.cards.filter((card) => {
      return card.visible && !card.preview;
    });
  }

  /**
   * list of visible cards that can be shown in review
   */
  public get reviewableCards(): CardInstance[] {
    const cards = this.pageVisibleCards.filter((card) => !card.hideInReview);
    return cards;
  }

  /**
   * return list of all visible fields in the page including nested fields
   */
  public get pageVisibleFields(): FieldInstance[] {
    const fields: FieldInstance[] = [];
    for (const card of this.pageVisibleCards || []) {
      fields.push(...card.helper.flatVisibleFields);
    }
    return fields;
  }
}
